import {
  resolvePreset,
  type DateRange,
  type DateRangePreset,
} from '@/components/shared/date-range-filter';

export const PLEDGE_STATUS_VALUES = ['active', 'fulfilled', 'cancelled', 'lapsed'] as const;

export type PledgeStatusFilter = (typeof PLEDGE_STATUS_VALUES)[number];

export interface PledgesPagePreferences {
  search: string;
  frequency: string;
  statuses: PledgeStatusFilter[];
  dateRangePreset: DateRangePreset;
  dateFrom: string | null;
  dateTo: string | null;
}

const DEFAULT_PREFERENCES: PledgesPagePreferences = {
  search: '',
  frequency: 'any',
  statuses: [],
  dateRangePreset: 'allTime',
  dateFrom: null,
  dateTo: null,
};

const DATE_RANGE_PRESETS = new Set<DateRangePreset>([
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'thisYear',
  'lastYear',
  'allTime',
  'custom',
]);

function getPreferencesStorageKey(churchId?: string) {
  return churchId
    ? `giving-pledges-page-preferences:${churchId}`
    : 'giving-pledges-page-preferences';
}

function parseStatuses(value: unknown): PledgeStatusFilter[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is PledgeStatusFilter =>
      typeof item === 'string' &&
      (PLEDGE_STATUS_VALUES as readonly string[]).includes(item),
  );
}

export function resolveStoredPledgeDateRange(
  preferences: Pick<PledgesPagePreferences, 'dateRangePreset' | 'dateFrom' | 'dateTo'>,
  timezone: string,
): DateRange {
  const preset = DATE_RANGE_PRESETS.has(preferences.dateRangePreset)
    ? preferences.dateRangePreset
    : 'allTime';
  if (preset === 'custom') {
    return {
      preset: 'custom',
      from: preferences.dateFrom,
      to: preferences.dateTo,
    };
  }
  return {
    preset,
    ...resolvePreset(preset, timezone),
  };
}

export function loadPledgesPagePreferences(churchId?: string): PledgesPagePreferences {
  if (typeof window === 'undefined') {
    return DEFAULT_PREFERENCES;
  }

  try {
    const raw = window.localStorage.getItem(getPreferencesStorageKey(churchId));
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<PledgesPagePreferences>;
    const dateRangePreset = DATE_RANGE_PRESETS.has(parsed.dateRangePreset as DateRangePreset)
      ? (parsed.dateRangePreset as DateRangePreset)
      : DEFAULT_PREFERENCES.dateRangePreset;

    return {
      search: typeof parsed.search === 'string' ? parsed.search : DEFAULT_PREFERENCES.search,
      frequency:
        typeof parsed.frequency === 'string' ? parsed.frequency : DEFAULT_PREFERENCES.frequency,
      statuses: parseStatuses(parsed.statuses),
      dateRangePreset,
      dateFrom: typeof parsed.dateFrom === 'string' ? parsed.dateFrom : null,
      dateTo: typeof parsed.dateTo === 'string' ? parsed.dateTo : null,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function persistPledgesPagePreferences(
  churchId: string | undefined,
  preferences: PledgesPagePreferences,
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getPreferencesStorageKey(churchId), JSON.stringify(preferences));
  } catch {
    // Ignore storage failures and fall back to the in-memory state for this session.
  }
}

export function toPledgesPagePreferences(input: {
  search: string;
  frequency: string;
  statuses: PledgeStatusFilter[];
  dateRange: DateRange;
}): PledgesPagePreferences {
  return {
    search: input.search,
    frequency: input.frequency,
    statuses: input.statuses,
    dateRangePreset: input.dateRange.preset,
    dateFrom: input.dateRange.from,
    dateTo: input.dateRange.to,
  };
}
